import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import type { Building, BuildingCreateInput, BuildingUpdateInput, StagedBuildingImage } from '@/hooks'
import { BuildingImageUploader } from './BuildingImageUploader'
import { BuildingImageGallery } from './BuildingImageGallery'
import { StagedBuildingImageUploader } from './StagedBuildingImageUploader'

// ============================================================================
// Form Schema
// ============================================================================

const buildingFormSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre es obligatorio')
    .max(255, 'El nombre debe tener 255 caracteres o menos'),
  slug: z
    .string()
    .min(1, 'El slug es obligatorio')
    .max(255, 'El slug debe tener 255 caracteres o menos')
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'El slug debe ser en minusculas solo con guiones (ej., mi-edificio)'
    ),
  status: z.enum(['draft', 'published', 'archived']),
  description_short: z.string().max(500, 'La descripcion corta debe tener 500 caracteres o menos').optional().or(z.literal('')),
  description_long: z.string().max(10000, 'La descripcion larga debe tener 10,000 caracteres o menos').optional().or(z.literal('')),
  // Location
  address: z.string().max(255, 'La direccion debe tener 255 caracteres o menos').optional().or(z.literal('')),
  city: z.string().max(100, 'La ciudad debe tener 100 caracteres o menos').optional().or(z.literal('')),
  province: z.string().max(100, 'La provincia debe tener 100 caracteres o menos').optional().or(z.literal('')),
  region: z.string().max(100, 'La zona debe tener 100 caracteres o menos').optional().or(z.literal('')),
  postal_code: z.string().max(20, 'El codigo postal debe tener 20 caracteres o menos').optional().or(z.literal('')),
  // Options
  featured: z.boolean(),
  badge: z.string().max(100, 'La etiqueta debe tener 100 caracteres o menos').optional().or(z.literal('')),
})

type BuildingFormData = z.infer<typeof buildingFormSchema>

// ============================================================================
// Slug Generation Utility
// ============================================================================

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

// ============================================================================
// Form Component
// ============================================================================

interface BuildingFormPropsBase {
  /** Existing building data for edit mode */
  building?: Building
  /** Delete handler (edit mode only) */
  onDelete?: () => void
  /** Whether form is submitting */
  isSubmitting?: boolean
  /** Whether delete is in progress */
  isDeleting?: boolean
  /** Submission error message */
  submitError?: string | null
}

interface BuildingFormCreateProps extends BuildingFormPropsBase {
  /** Whether form is in edit mode */
  isEdit?: false
  /** Submit handler for create - includes staged images */
  onSubmit: (data: BuildingCreateInput, stagedImages: StagedBuildingImage[]) => void
}

interface BuildingFormEditProps extends BuildingFormPropsBase {
  /** Whether form is in edit mode */
  isEdit: true
  /** Submit handler for update */
  onSubmit: (data: BuildingUpdateInput) => void
}

type BuildingFormProps = BuildingFormCreateProps | BuildingFormEditProps

export function BuildingForm({
  building,
  isEdit = false,
  onSubmit,
  onDelete,
  isSubmitting = false,
  isDeleting = false,
  submitError,
}: BuildingFormProps) {
  // Staged images state for create mode
  const [stagedImages, setStagedImages] = useState<StagedBuildingImage[]>([])

  const defaultValues: BuildingFormData = useMemo(() => ({
    name: building?.name ?? '',
    slug: building?.slug ?? '',
    status: building?.status ?? 'draft',
    description_short: building?.description_short ?? '',
    description_long: building?.description_long ?? '',
    address: building?.address ?? '',
    city: building?.city ?? '',
    province: building?.province ?? '',
    region: building?.region ?? '',
    postal_code: building?.postal_code ?? '',
    featured: building?.featured ?? false,
    badge: building?.badge ?? '',
  }), [building])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<BuildingFormData>({
    resolver: zodResolver(buildingFormSchema),
    defaultValues,
  })

  // Track if user has manually edited the slug field
  const slugManuallyEdited = useRef(false)
  const lastAutoSlug = useRef('')

  // Reset form when building data changes to sync dirty tracking baseline
  useEffect(() => {
    reset(defaultValues)
    // Reset slug tracking on form reset
    slugManuallyEdited.current = false
    lastAutoSlug.current = ''
  }, [defaultValues, reset])

  // Watch name for auto-slug generation
  const name = watch('name')

  // Auto-generate slug from name (only in create mode and if slug hasn't been manually edited)
  useEffect(() => {
    if (!isEdit && name && !slugManuallyEdited.current) {
      const generatedSlug = generateSlug(name)
      lastAutoSlug.current = generatedSlug
      setValue('slug', generatedSlug, { shouldValidate: true })
    }
  }, [name, isEdit, setValue])

  // Handle manual slug edits
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    // If user types something different from what auto-generation would produce, mark as manually edited
    if (newValue !== lastAutoSlug.current) {
      slugManuallyEdited.current = true
    }
    // If user clears the field, re-enable auto-generation
    if (newValue === '') {
      slugManuallyEdited.current = false
    }
  }

  // Transform form data to API format
  function transformFormData(data: BuildingFormData): BuildingCreateInput {
    return {
      name: data.name,
      slug: data.slug,
      status: data.status,
      description_short: data.description_short || undefined,
      description_long: data.description_long || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      province: data.province || undefined,
      region: data.region || undefined,
      postal_code: data.postal_code || undefined,
      featured: data.featured,
      badge: data.badge || undefined,
    }
  }

  function handleFormSubmit(data: BuildingFormData) {
    const transformedData = transformFormData(data)

    if (isEdit) {
      // Edit mode - just pass the data
      ;(onSubmit as (data: BuildingUpdateInput) => void)(transformedData)
    } else {
      // Create mode - pass data and staged images
      ;(onSubmit as (data: BuildingCreateInput, stagedImages: StagedBuildingImage[]) => void)(transformedData, stagedImages)
    }
  }

  const isDisabled = isSubmitting || isDeleting

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {/* Error Alert */}
      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <strong>Error:</strong> {submitError}
        </div>
      )}

      {/* Basic Information */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Informacion Basica</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              placeholder="Residencial Costa Azul"
              disabled={isDisabled}
              {...register('name')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="slug" className="block text-sm font-medium">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              id="slug"
              type="text"
              placeholder="residencial-costa-azul"
              disabled={isDisabled}
              {...register('slug', {
                onChange: handleSlugChange,
              })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Identificador amigable para URL (generado automaticamente del nombre)
            </p>
            {errors.slug && (
              <p className="mt-1 text-xs text-red-500">{errors.slug.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium">
              Estado
            </label>
            <select
              id="status"
              disabled={isDisabled}
              {...register('status')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="draft">Borrador</option>
              <option value="published">Publicado</option>
              <option value="archived">Archivado</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-xs text-red-500">{errors.status.message}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label htmlFor="badge" className="block text-sm font-medium">
              Etiqueta
            </label>
            <input
              id="badge"
              type="text"
              placeholder="Promoción | Obra Nueva"
              disabled={isDisabled}
              {...register('badge')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Etiqueta opcional para mostrar en tarjetas (ej., "Promoción | Obra Nueva", "Segunda Mano")
            </p>
            {errors.badge && (
              <p className="mt-1 text-xs text-red-500">{errors.badge.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Descriptions */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Descripciones</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="description_short" className="block text-sm font-medium">
              Descripcion Corta
            </label>
            <textarea
              id="description_short"
              rows={2}
              placeholder="Un breve resumen del edificio..."
              disabled={isDisabled}
              {...register('description_short')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.description_short && (
              <p className="mt-1 text-xs text-red-500">{errors.description_short.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="description_long" className="block text-sm font-medium">
              Descripcion Larga
            </label>
            <textarea
              id="description_long"
              rows={6}
              placeholder="Descripcion detallada del edificio y sus amenidades..."
              disabled={isDisabled}
              {...register('description_long')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.description_long && (
              <p className="mt-1 text-xs text-red-500">{errors.description_long.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Ubicacion</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium">
              Direccion
            </label>
            <input
              id="address"
              type="text"
              placeholder="Calle Principal 123"
              disabled={isDisabled}
              {...register('address')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.address && (
              <p className="mt-1 text-xs text-red-500">{errors.address.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="city" className="block text-sm font-medium">
              Ciudad
            </label>
            <input
              id="city"
              type="text"
              placeholder="Alicante"
              disabled={isDisabled}
              {...register('city')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.city && (
              <p className="mt-1 text-xs text-red-500">{errors.city.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="province" className="block text-sm font-medium">
              Provincia
            </label>
            <input
              id="province"
              type="text"
              placeholder="Alicante"
              disabled={isDisabled}
              {...register('province')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.province && (
              <p className="mt-1 text-xs text-red-500">{errors.province.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="region" className="block text-sm font-medium">
              Zona
            </label>
            <input
              id="region"
              type="text"
              placeholder="Costa Blanca"
              disabled={isDisabled}
              {...register('region')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.region && (
              <p className="mt-1 text-xs text-red-500">{errors.region.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium">
              Codigo Postal
            </label>
            <input
              id="postal_code"
              type="text"
              placeholder="03001"
              disabled={isDisabled}
              {...register('postal_code')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.postal_code && (
              <p className="mt-1 text-xs text-red-500">{errors.postal_code.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Options */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Opciones</h2>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            disabled={isDisabled}
            {...register('featured')}
            className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-sm">Edificio destacado (mostrado en la pagina principal)</span>
        </label>
        {errors.featured && (
          <p className="mt-1 text-xs text-red-500">{errors.featured.message}</p>
        )}
      </section>

      {/* Images */}
      {isEdit && building?._id ? (
        <div className="space-y-6">
          <BuildingImageUploader buildingId={building._id} disabled={isDisabled} />
          <BuildingImageGallery buildingId={building._id} disabled={isDisabled} />
        </div>
      ) : (
        <StagedBuildingImageUploader
          images={stagedImages}
          onImagesChange={setStagedImages}
          disabled={isDisabled}
        />
      )}

      {/* Actions */}
      <div className={`flex items-center ${isEdit ? 'justify-between' : 'justify-end'}`}>
        {isEdit && onDelete && (
          <button
            type="button"
            onClick={() => {
              if (confirm('Estas seguro de que quieres eliminar este edificio? Esta accion no se puede deshacer. Se eliminaran tambien todas las imagenes asociadas.')) {
                onDelete()
              }
            }}
            disabled={isDisabled}
            className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Eliminar Edificio
          </button>
        )}
        <div className="flex items-center gap-4">
          <Link
            to="/buildings"
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isDisabled}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Guardar Cambios' : 'Crear Edificio'}
          </button>
        </div>
      </div>
    </form>
  )
}
